import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trans, useTranslation } from "react-i18next";
import { MessageSquareHeart, X, Star, Send } from "lucide-react";
import { toast } from "sonner";
import { submitFeedback } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { track } from "../lib/analytics";

/**
 * Floating "Send feedback" pill that lives on every page.
 * Anyone can submit (signed in or not). Submissions land in `db.feedback`
 * and are visible to admins (DEV_EMAILS) at /admin/feedback.
 */
const FeedbackWidget = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setRating(0);
    setHover(0);
    setMessage("");
    setEmail("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error(t("feedback.needMessage"));
      return;
    }
    setSubmitting(true);
    try {
      await submitFeedback({
        message: message.trim(),
        rating: rating || null,
        email: email.trim() || null,
        page_url: typeof window !== "undefined" ? window.location.pathname : null,
      });
      toast.success(t("feedback.sent"));
      track("feedback_submitted", { has_rating: !!rating, has_email: !!email });
      reset();
      setOpen(false);
    } catch {
      toast.error(t("feedback.sendFail"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating launcher. On mobile we sit above the MobileTabBar
          (~64px tall + safe-area inset). On desktop we leave room for
          the persistent Privacy choices pill below it. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-24 sm:bottom-16 left-4 sm:left-6 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-[#5D4050] text-white shadow-[0_10px_30px_rgba(93,64,80,0.35)] hover:-translate-y-0.5 hover:bg-[#4a3340] transition-all"
        data-testid="feedback-launcher-btn"
        aria-label={t("onboarding.feedback.launcher")}
      >
        <MessageSquareHeart className="h-4 w-4" />
        <span className="text-sm font-medium">
          {t("onboarding.feedback.launcher")}
        </span>
      </button>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 py-6 bg-[#2C2123]/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="feedback-modal"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ type: "spring", stiffness: 240, damping: 24 }}
              className="w-full max-w-md bg-white rounded-3xl border border-[#EADDD7] p-6 sm:p-7 soft-shadow-lg relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 h-8 w-8 rounded-full hover:bg-[#F5EFEB] flex items-center justify-center text-[#8A7D82] transition-colors"
                aria-label={t("feedback.closeAria")}
                data-testid="feedback-close-btn"
              >
                <X className="h-4 w-4" />
              </button>

              <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-2">
                {t("feedback.kicker")}
              </span>
              <h3 className="font-serif italic text-2xl text-[#2C2123] leading-tight mb-1">
                {t("feedback.headlineBefore")} <em>{t("feedback.headlineEmphasis")}</em>?
              </h3>
              <p className="text-sm text-[#5C4B51] mb-5">
                {t("feedback.intro")}
              </p>

              <form onSubmit={onSubmit} className="space-y-4">
                {/* Rating */}
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#8A7D82] mb-2">
                    {t("feedback.ratingLabel")} <span className="normal-case tracking-normal text-[#B8A8AB]">{t("feedback.optional")}</span>
                  </div>
                  <div className="flex items-center gap-1" data-testid="feedback-rating-row">
                    {[1, 2, 3, 4, 5].map((n) => {
                      const filled = (hover || rating) >= n;
                      return (
                        <button
                          type="button"
                          key={n}
                          onMouseEnter={() => setHover(n)}
                          onMouseLeave={() => setHover(0)}
                          onClick={() => setRating(n === rating ? 0 : n)}
                          className="p-1 transition-transform hover:scale-110"
                          data-testid={`feedback-star-${n}`}
                          aria-label={t("feedback.ratingStarAria", { n })}
                        >
                          <Star
                            className={`h-6 w-6 ${
                              filled
                                ? "fill-[#D4B483] stroke-[#D4B483]"
                                : "stroke-[#C9A8A4]"
                            }`}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="feedback-message"
                    className="text-xs uppercase tracking-[0.18em] text-[#8A7D82] mb-2 block"
                  >
                    {t("feedback.thoughtsLabel")}
                  </label>
                  <textarea
                    id="feedback-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    maxLength={4000}
                    required
                    placeholder={t("feedback.thoughtsPlaceholder")}
                    className="w-full rounded-xl border border-[#EADDD7] bg-[#FDFBF7] px-4 py-3 text-[15px] text-[#2C2123] placeholder:text-[#B8A8AB] focus:outline-none focus:border-[#9B6A6C] focus:ring-2 focus:ring-[#F2E8E9] transition-all resize-none"
                    data-testid="feedback-message-input"
                  />
                </div>

                {/* Email — optional, prefilled if signed in */}
                {!user && (
                  <div>
                    <label
                      htmlFor="feedback-email"
                      className="text-xs uppercase tracking-[0.18em] text-[#8A7D82] mb-2 block"
                    >
                      {t("feedback.emailLabel")} <span className="normal-case tracking-normal text-[#B8A8AB]">{t("feedback.emailOptional")}</span>
                    </label>
                    <input
                      id="feedback-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={200}
                      placeholder={t("feedback.emailPlaceholder")}
                      className="w-full rounded-xl border border-[#EADDD7] bg-[#FDFBF7] px-4 py-3 text-[15px] text-[#2C2123] placeholder:text-[#B8A8AB] focus:outline-none focus:border-[#9B6A6C] focus:ring-2 focus:ring-[#F2E8E9] transition-all"
                      data-testid="feedback-email-input"
                    />
                  </div>
                )}

                {user && (
                  <p className="text-xs text-[#8A7D82]">
                    <Trans
                      i18nKey="feedback.submittingAs"
                      values={{ email: user.email }}
                      components={{ em: <span className="text-[#5D4050]" /> }}
                    />
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] disabled:opacity-60 rounded-full px-6 py-3 transition-all shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5"
                  data-testid="feedback-submit-btn"
                >
                  <Send className="h-4 w-4" />
                  {submitting
                    ? t("onboarding.feedback.sending")
                    : t("onboarding.feedback.launcher")}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FeedbackWidget;
